
import { SQLModule } from '../types';

export const SQL_ADVANCED_MODULES: SQLModule[] = [
  {
    id: 'sql-advanced-1',
    title: 'Subqueries and CTEs',
    description: 'Learn advanced query techniques with subqueries and Common Table Expressions.',
    content: `# Subqueries and Common Table Expressions (CTEs)

Advanced SQL techniques for complex data analysis and manipulation.

## Subqueries

A subquery is a query nested inside another query.

### Scalar Subqueries
Returns a single value:

\`\`\`sql
-- Find users with above-average salary
SELECT name, salary
FROM employees
WHERE salary > (SELECT AVG(salary) FROM employees);
\`\`\`

### Correlated Subqueries
References columns from the outer query:

\`\`\`sql
-- Find employees earning more than their department average
SELECT name, salary, department
FROM employees e1
WHERE salary > (
    SELECT AVG(salary)
    FROM employees e2
    WHERE e2.department = e1.department
);
\`\`\`

### EXISTS and NOT EXISTS
\`\`\`sql
-- Find customers who have placed orders
SELECT name
FROM customers c
WHERE EXISTS (
    SELECT 1 FROM orders o WHERE o.customer_id = c.id
);
\`\`\`

## Common Table Expressions (CTEs)

CTEs provide a way to write auxiliary statements for use in a larger query:

\`\`\`sql
WITH department_stats AS (
    SELECT 
        department,
        AVG(salary) as avg_salary,
        COUNT(*) as employee_count
    FROM employees
    GROUP BY department
)
SELECT 
    e.name,
    e.salary,
    ds.avg_salary,
    e.salary - ds.avg_salary as salary_diff
FROM employees e
JOIN department_stats ds ON e.department = ds.department;
\`\`\`

## Recursive CTEs

For hierarchical data:

\`\`\`sql
WITH RECURSIVE employee_hierarchy AS (
    -- Base case: top-level managers
    SELECT id, name, manager_id, 0 as level
    FROM employees
    WHERE manager_id IS NULL
    
    UNION ALL
    
    -- Recursive case: employees with managers
    SELECT e.id, e.name, e.manager_id, eh.level + 1
    FROM employees e
    JOIN employee_hierarchy eh ON e.manager_id = eh.id
)
SELECT * FROM employee_hierarchy;
\`\`\``,
    duration: '70 min',
    estimatedTime: 70,
    difficulty: 'advanced',
    order: 1
  },
  {
    id: 'sql-advanced-2',
    title: 'Performance Optimization',
    description: 'Learn query optimization techniques and indexing strategies.',
    content: `# Query Performance Optimization

Techniques to improve SQL query performance and database efficiency.

## Understanding Execution Plans

Use EXPLAIN to analyze query execution:

\`\`\`sql
EXPLAIN SELECT * FROM users WHERE email = 'john@example.com';
\`\`\`

## Indexing Strategies

### Creating Indexes
\`\`\`sql
-- Single column index
CREATE INDEX idx_email ON users(email);

-- Composite index
CREATE INDEX idx_name_department ON employees(last_name, department);

-- Unique index
CREATE UNIQUE INDEX idx_username ON users(username);
\`\`\`

### When to Use Indexes
- Frequently searched columns
- Foreign key columns
- Columns used in ORDER BY
- Columns used in WHERE clauses

### When NOT to Use Indexes
- Small tables
- Frequently updated columns
- Tables with high INSERT/UPDATE activity

## Query Optimization Techniques

### Use Appropriate WHERE Clauses
\`\`\`sql
-- Good: Uses index
SELECT * FROM orders WHERE order_date >= '2024-01-01';

-- Bad: Function on column prevents index use
SELECT * FROM orders WHERE YEAR(order_date) = 2024;
\`\`\`

### Limit Result Sets
\`\`\`sql
-- Use LIMIT for pagination
SELECT * FROM products ORDER BY name LIMIT 20 OFFSET 40;
\`\`\`

### Avoid SELECT *
\`\`\`sql
-- Good: Only select needed columns
SELECT id, name, email FROM users;

-- Bad: Retrieves unnecessary data
SELECT * FROM users;
\`\`\`

## Advanced Optimization

### Partitioning
For very large tables:

\`\`\`sql
-- Range partitioning by date
CREATE TABLE orders_2024 PARTITION OF orders
FOR VALUES FROM ('2024-01-01') TO ('2025-01-01');
\`\`\`

### Query Hints (Database-specific)
\`\`\`sql
-- Force index usage (MySQL)
SELECT * FROM users USE INDEX (idx_email) WHERE email = 'john@example.com';
\`\`\``,
    duration: '80 min',
    estimatedTime: 80,
    difficulty: 'advanced',
    order: 2
  }
];
